<script context="module" lang="ts">
  export type MapType = 'svg' | 'visioweb';

</script>

<script lang="ts">
  import { getStoresContext, type StoresKey } from "~/stores";
  import { ConnectMapApp, type ConnectMapEvent, ImplLayer, type ConnectMapEventHandler } from "./app";
  import { createEventDispatcher, onMount, setContext } from "svelte";
  import BtnControl from "./BtnControl.svelte";
  import FloorControl from "./FloorControl.svelte";
  import Overlay from "./Overlay.svelte";
  import ViewControl from "./ViewControl.svelte";
  import TopBar from "./TopBar.svelte";
  import Icon from "./Icon.svelte";
  import SidePane from "./side/SidePane.svelte";
  import { type POI } from "./poi";
  import BottomPane from "./BottomPane.svelte";
  import UI from "./UI.svelte";
  import { BROWSER } from "esm-env";

  export let map_layer: ConstructorOfATypedSvelteComponent;
  export let app: ConnectMapApp<ImplLayer>;
  export let request_store_key: StoresKey;

  let window_width: number;


  let dispatch = createEventDispatcher<ConnectMapEvent>()

  const { stores } = getStoresContext(request_store_key);
  const { fullscreen, search_term, state, is_desktop, is_mobile, T } = app;

  onMount(() => {
    const unsubscribe = stores.subscribe(xs => app.setStores(xs));
    return () => {
      unsubscribe();
      app.destroy();
    }
  });
  
  setContext<ConnectMapApp<any>>('app', app);

  $: if (BROWSER) {
    dispatch('fullscreen', $fullscreen);
    requestAnimationFrame(() => app.impl_layer?.resize());
  }
  $: if (app) {
    $is_desktop = window_width >= app.desktop_breakpoint;
  }

  $: {
    app.updateSearch($search_term);
  }

  const handleEvent: ConnectMapEventHandler = ({type, detail}) => {    
    switch (type) {
      case 'tap_poi': app.state.selectPOI(detail as POI); break;
    }

    // Bubble
    dispatch(type, detail);
  }

  function resize () {
    requestAnimationFrame(() => app.impl_layer?.resize());
  }
  
</script>

<svelte:window bind:innerWidth={window_width} on:resize={resize} />

<div class="map-app" data-state={$state}>
  <UI>
  <ViewControl>
    <!-- <slot>Map layer</slot> -->
    <svelte:component this={map_layer} on:tap_poi={handleEvent}  />
    <svelte:fragment slot="top">
      <TopBar float={true} on:focus={app.state.showListing} />
    </svelte:fragment>
    <svelte:fragment slot="close">
      <BtnControl on:click={app.state.exit} rounding={100}>
        <Icon name="exit" />
      </BtnControl>
    </svelte:fragment>
    <svelte:fragment slot="floor">
      <FloorControl />
    </svelte:fragment>
    <svelte:fragment slot="side">
      <SidePane />
    </svelte:fragment>
    <svelte:fragment slot="bottom">
      {#if $is_mobile}
        <BottomPane />
      {/if}
    </svelte:fragment>
  </ViewControl>
  </UI>
  <Overlay on:dblclick={app.state.toggle}>
    <p>{$T`overlay_instruction`}</p>
  </Overlay>
</div>

<style>
  .map-app {
    position: relative;
    width: 100%;
    height: 100%;

    --_bg-1st: var(--bg-1st, #fff);
    --_bg-2nd: var(--bg-2nd, #efefef);

    --_btn-pri-bg: var(--btn-pri-bg, #000);
    --_btn-pri-c: var(--btn-pri-c, #fff);

    --_p1: var(--p1, 8px);
    --_p2: calc(var(--_p1) * 2);
    --_p3: calc(var(--_p1) * 3);
    --_p4: calc(var(--_p1) * 4);

    --_topbar-border-c: var(--topbar-border-c, #ccc);

    --vgwv-primary-color: #000;
    --vgwv-primary-color-invert: #fff;

    --vgwv-primary-btn--margin: 8px 0px;
    --vgwv-primary-btn--_pding: 7px 16px 9px;

    --vgwv-shadow-sharp--color: rgba(0,0,0,.15);
    --vgwv-shadow-blur--color: rgba(0,0,0,.25);

    --vgwv-shadow-up:   0 -1px .25px 1.25px var(--vgwv-shadow-sharp--color),
                        0 -4px 8px var(--vgwv-shadow-blur--color);
    --vgwv-shadow-down: 0 1px .25px 1.25px var(--vgwv-shadow-sharp--color),
                          0 4px 8px var(--vgwv-shadow-blur--color);
    --vgwv-shadow-off:  0 0 0 0 var(--vgwv-shadow-sharp--color),
                        0 0 0 var(--vgwv-shadow-blur--color);

  }
</style>